import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Loader2, Download, ArrowLeft, ArrowDownLeft, ArrowUpRight, Wallet,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { formatGBP } from "@/lib/marketplace";
import { useAuth } from "@/context/AuthContext";

/**
 * Iteration 81 — Financial Wallet Statement.
 *
 * A dedicated bank-statement view for the household wallet (separate from
 * the marketplace ledger). Renders:
 *   - Profile filter (Household / per-kid)
 *   - Transaction Type filter
 *   - Date range filters
 *   - Running balance column (cumulative from oldest → newest)
 *   - Export Statement (CSV) button
 *
 * Data source: existing `GET /api/wallet/history?limit=200` proxy (which
 * returns `{transactions: [...]}` from upstream) plus `GET /api/kids`
 * for the profile filter dropdown.
 */

const TX_TYPES = [
  { value: "all",               label: "All transaction types" },
  { value: "topup",             label: "Top-ups" },
  { value: "transfer_to_kid",   label: "Transfers to kids" },
  { value: "transfer_from_kid", label: "Transfers from kids (cash-outs)" },
  { value: "payout",            label: "Payouts to bank" },
  { value: "purchase",          label: "Purchases" },
  { value: "fee",               label: "Fees" },
];

// Iteration 279 — cash-out detector. A child cash-out (kid → parent
// wallet transfer) must NEVER be mistaken for a transfer-to-kid: from
// the Household perspective the money ARRIVES. Detection uses explicit
// kind slugs first, then the counterparty endpoint type, then the
// legacy "From <Kid Name>" description shape.
const CASHOUT_KINDS = [
  "transfer_to_parent", "kid_to_parent", "sent_to_parent",
  "transfer_from_kid", "cashout", "cash_out",
  "cashout_to_parent", "wallet_transfer_to_parent",
];
const isCashoutFromKid = (t) => {
  const raw = (t?.type || t?.kind || "").toLowerCase();
  if (raw.includes("to_kid") || raw.includes("topup") || raw.includes("top_up")) return false;
  if (CASHOUT_KINDS.some((k) => raw.includes(k))) return true;
  const fromType = (t?.from?.type || "").toLowerCase();
  if (fromType === "kid" || fromType === "child") return true;
  const desc = (t?.description || t?.note || "").trim();
  if (/^from\s+\S+/i.test(desc) && (t?.from?.id || t?.kid_id || t?.related_kid_id)) return true;
  return false;
};

const normaliseType = (t) => {
  if (isCashoutFromKid(t)) return "transfer_from_kid";
  const raw = (t?.type || t?.kind || "").toLowerCase();
  if (raw.includes("topup") || raw.includes("top_up")) return "topup";
  if (raw.includes("transfer") || raw.includes("pocket")) return "transfer_to_kid";
  if (raw.includes("payout") || raw.includes("withdraw")) return "payout";
  if (raw.includes("purchase") || raw.includes("order") || raw.includes("buy")) return "purchase";
  if (raw.includes("fee") || raw.includes("charge")) return "fee";
  return "other";
};

const asDate = (t) => t.created_at || t.timestamp || t.date || "";

function toCSV(rows) {
  const header = ["Date", "Description", "Profile", "Type", "Amount (£)", "Direction", "Running Balance (£)"];
  const lines = [header.join(",")];
  rows.forEach((r) => {
    const amt = ((r._pennies || 0) / 100).toFixed(2);
    const bal = ((r._runningPennies || 0) / 100).toFixed(2);
    const cells = [
      (asDate(r) || "").slice(0, 19),
      `"${(r.description || r.note || r.type || "Transaction").replace(/"/g, '""')}"`,
      `"${(r._profileLabel || "Household").replace(/"/g, '""')}"`,
      normaliseType(r),
      amt,
      r._isCredit ? "credit" : "debit",
      bal,
    ];
    lines.push(cells.join(","));
  });
  return lines.join("\n");
}

export default function WalletStatement() {
  const { user } = useAuth();
  const [txns, setTxns] = useState([]);
  const [kids, setKids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [profile, setProfile] = useState("all");
  const [txType, setTxType] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  useEffect(() => {
    if (!user?.id) return;
    (async () => {
      setLoading(true);
      try {
        const [w, k] = await Promise.all([
          api.get("/wallet/history?limit=200"),
          api.get("/kids"),
        ]);
        const raw = Array.isArray(w.data?.transactions) ? w.data.transactions : (Array.isArray(w.data) ? w.data : []);
        setTxns(raw);
        setKids(k.data || []);
      } catch (e) {
        setError(formatApiError(e) || "Couldn't load your wallet statement.");
      } finally {
        setLoading(false);
      }
    })();
  }, [user?.id]);

  // Enrich + sort oldest→newest so running balance builds correctly,
  // then reverse for display (newest first).
  const rows = useMemo(() => {
    const enriched = txns.map((t) => {
      const pennies = Math.abs(Number(t.amount_pennies ?? t.pennies ?? 0));
      const type = normaliseType(t);
      // Iteration 279 — cash-outs from a child are ALWAYS a household
      // credit (+£X.XX, green incoming arrow), even if upstream tagged
      // the row from the kid's perspective (direction "debit").
      const cashout = isCashoutFromKid(t);
      const isCredit = cashout ? true : (t.direction ? t.direction === "credit" : !(type === "transfer_to_kid" || type === "payout" || type === "purchase" || type === "fee"));
      const fromType = (t?.from?.type || "").toLowerCase();
      const kidId = t.kid_id || t.related_kid_id
        || (cashout && (fromType === "kid" || fromType === "child") ? t.from.id : null)
        || null;
      const profileLabel = kidId
        ? (kids.find((k) => k.id === kidId)?.name || t.related_kid_name || "Kid")
        : "Household";
      return {
        ...t,
        _pennies: pennies,
        _isCredit: isCredit,
        _type: type,
        _kidId: kidId,
        _profileLabel: profileLabel,
      };
    });

    // sort ascending by date for running balance
    enriched.sort((a, b) => asDate(a).localeCompare(asDate(b)));
    let running = 0;
    enriched.forEach((r) => {
      running += r._isCredit ? r._pennies : -r._pennies;
      r._runningPennies = running;
    });
    // newest-first for the table
    enriched.reverse();
    return enriched;
  }, [txns, kids]);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (profile !== "all") {
        if (profile === "household" && r._kidId) return false;
        if (profile !== "household" && r._kidId !== profile) return false;
      }
      if (txType !== "all" && r._type !== txType) return false;
      const d = (asDate(r) || "").slice(0, 10);
      if (fromDate && d < fromDate) return false;
      if (toDate && d > toDate) return false;
      return true;
    });
  }, [rows, profile, txType, fromDate, toDate]);

  const totals = useMemo(() => {
    let credit = 0, debit = 0;
    filtered.forEach((r) => {
      if (r._isCredit) credit += r._pennies; else debit += r._pennies;
    });
    return { credit, debit, net: credit - debit, count: filtered.length };
  }, [filtered]);

  const currentBalance = rows.length ? rows[0]._runningPennies : 0;

  const exportCSV = () => {
    const csv = toCSV(filtered);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const stamp = new Date().toISOString().slice(0, 10);
    a.download = `wallet-statement-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white py-8 px-4" data-testid="wallet-statement-page">
      <div className="max-w-6xl mx-auto">
        <Link
          to="/dashboard/parent"
          className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-brandSlate mb-4"
          data-testid="wallet-statement-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back to dashboard
        </Link>

        <div className="flex items-start justify-between gap-4 flex-wrap mb-2">
          <div>
            <h1 className="text-3xl md:text-4xl font-display font-semibold text-slate-900 flex items-center gap-2">
              <Wallet className="w-8 h-8 text-brandSlate" /> Wallet Statement
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              Pure financial statement — top-ups, transfers, payouts and fees. Item listings live in the <Link to="/dashboard/ledger" className="text-brandSlate underline underline-offset-2 hover:text-brandSlateDark" data-testid="statement-to-ledger">Ledger &amp; Activity</Link> view.
            </p>
          </div>
          <button
            type="button"
            onClick={exportCSV}
            disabled={!filtered.length}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold bg-brandSlate text-white hover:bg-brandSlateDark disabled:opacity-60"
            data-testid="wallet-statement-export-csv"
          >
            <Download className="w-4 h-4" /> Export Statement (CSV)
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 my-6" data-testid="wallet-statement-summary">
          <div className="card-base p-4">
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Current balance</div>
            <div className="text-2xl font-display font-semibold text-brandSlate mt-1" data-testid="stat-current-balance">{formatGBP(currentBalance / 100)}</div>
          </div>
          <div className="card-base p-4">
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Money in (filtered)</div>
            <div className="text-2xl font-display font-semibold text-emerald-700 mt-1" data-testid="stat-money-in">{formatGBP(totals.credit / 100)}</div>
          </div>
          <div className="card-base p-4">
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Money out (filtered)</div>
            <div className="text-2xl font-display font-semibold text-rose-700 mt-1" data-testid="stat-money-out">{formatGBP(totals.debit / 100)}</div>
          </div>
          <div className="card-base p-4">
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Net (filtered)</div>
            <div className={`text-2xl font-display font-semibold mt-1 ${totals.net >= 0 ? "text-emerald-700" : "text-rose-700"}`} data-testid="stat-net">{formatGBP(totals.net / 100)}</div>
          </div>
        </div>

        <div className="card-base p-4 mb-4 grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="wallet-statement-filters">
          <label className="block">
            <span className="block text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1">Profile</span>
            <select
              value={profile}
              onChange={(e) => setProfile(e.target.value)}
              className="w-full rounded-2xl border border-slate-200 px-3 py-2 focus:border-brandSlate outline-none bg-white text-sm"
              data-testid="filter-profile"
            >
              <option value="all">All profiles</option>
              <option value="household">Household (parent)</option>
              {kids.map((k) => (
                <option key={k.id} value={k.id}>{k.name}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="block text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1">Transaction type</span>
            <select
              value={txType}
              onChange={(e) => setTxType(e.target.value)}
              className="w-full rounded-2xl border border-slate-200 px-3 py-2 focus:border-brandSlate outline-none bg-white text-sm"
              data-testid="filter-tx-type"
            >
              {TX_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="block text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1">From</span>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="w-full rounded-2xl border border-slate-200 px-3 py-2 focus:border-brandSlate outline-none bg-white text-sm"
              data-testid="filter-from-date"
            />
          </label>
          <label className="block">
            <span className="block text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1">To</span>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="w-full rounded-2xl border border-slate-200 px-3 py-2 focus:border-brandSlate outline-none bg-white text-sm"
              data-testid="filter-to-date"
            />
          </label>
        </div>

        {loading ? (
          <Loader2 className="w-6 h-6 animate-spin text-slate-500 mx-auto my-8" />
        ) : error ? (
          <div className="card-base p-4 text-sm text-rose-700 bg-rose-50 border-rose-100" data-testid="wallet-statement-error">{error}</div>
        ) : !filtered.length ? (
          <div className="card-base p-8 text-center text-sm text-slate-500" data-testid="wallet-statement-empty">
            No transactions match your filters. Adjust the profile, type, or date range above.
          </div>
        ) : (
          <div className="card-base overflow-x-auto" data-testid="wallet-statement-table">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-600">
                <tr>
                  <th className="text-left px-4 py-3 font-bold">Date</th>
                  <th className="text-left px-4 py-3 font-bold">Description</th>
                  <th className="text-left px-4 py-3 font-bold">Profile</th>
                  <th className="text-left px-4 py-3 font-bold">Type</th>
                  <th className="text-right px-4 py-3 font-bold">Amount</th>
                  <th className="text-right px-4 py-3 font-bold">Balance</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, idx) => {
                  const when = asDate(r);
                  const dateTxt = when ? new Date(when).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—";
                  const label = r.description || r.note || r.type || "Transaction";
                  return (
                    <tr key={r.id || idx} className="border-t border-slate-100" data-testid={`wallet-statement-row-${r.id || idx}`}>
                      <td className="px-4 py-3 whitespace-nowrap text-[12px] text-slate-600">{dateTxt}</td>
                      <td className="px-4 py-3 text-slate-900 font-medium">
                        <div className="inline-flex items-center gap-2">
                          <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full shrink-0 ${r._isCredit ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
                            {r._isCredit ? <ArrowDownLeft className="w-3.5 h-3.5" /> : <ArrowUpRight className="w-3.5 h-3.5" />}
                          </span>
                          {label}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-700">{r._profileLabel}</td>
                      <td className="px-4 py-3 text-slate-700">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${r._type === "transfer_from_kid" ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-slate-100 text-slate-700 border-slate-200"}`}
                          data-testid={`wallet-statement-type-${r.id || idx}`}
                        >
                          {r._type.replace(/_/g, " ")}
                        </span>
                      </td>
                      <td className={`px-4 py-3 text-right font-display font-semibold whitespace-nowrap ${r._isCredit ? "text-emerald-700" : "text-rose-700"}`} data-testid={`wallet-statement-amount-${r.id || idx}`}>
                        {r._isCredit ? "+" : "-"}{formatGBP(r._pennies / 100)}
                      </td>
                      <td className="px-4 py-3 text-right font-display font-semibold text-slate-900 whitespace-nowrap" data-testid={`wallet-statement-balance-${r.id || idx}`}>
                        {formatGBP((r._runningPennies || 0) / 100)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
